import {Trans} from '@lingui/macro';
import React, {FC, useCallback} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {SimpleDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {SimpleDropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdownItem';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useClipboard} from '../../../../../helpers/hooks/clipboardHookHelpers';
import {buildCalendarEventPublicId} from '../../../../../helpers/models/calendarEventHelpers';
import {
  CalendarEventModel,
  CalendarEventVisualStatusesEnum,
} from '../../../../../models/api/calendarEventModel';

/*
 * Props.
 */

export interface CalendarInviteViewerMoreProps {
  calendarEvent: CalendarEventModel;
  hasICSAttachments: boolean;
  downloadICSAttachments: Handler;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  margin: 0 1px;
`;

const StyledMoreDiv = styled.div`
  transform: rotateZ(90deg);
`;

/*
 * Component.
 */

export const CalendarInviteViewerMore: FC<CalendarInviteViewerMoreProps> = (props) => (
  <StyledWrapperDiv>
    <PopoverCoordinator
      renderButton={renderButton}
      renderModal={(popoverProps) => <CalendarInviteViewerMoreDropdown {...popoverProps} {...props} />}
    />
  </StyledWrapperDiv>
);

/*
 * Button.
 */

function renderButton(isOpen: boolean) {
  return (
    <StyledMoreDiv>
      <IconButton iconName="more" label={<Trans id="calendar-invite-more">More</Trans>} hasHover={isOpen} />
    </StyledMoreDiv>
  );
}

/*
 * Dropdown.
 */

const CalendarInviteViewerMoreDropdown: FC<PopoverTransferProps & CalendarInviteViewerMoreProps> = (
  props,
) => {
  const {copy} = useClipboard();
  const {calendarEvent} = props;

  /*
   * Event handlers.
   */

  const onCopyEventIdSelect = useCallback(() => {
    if (calendarEvent.visualStatus === CalendarEventVisualStatusesEnum.DRAFT) {
      return;
    }

    copy(buildCalendarEventPublicId(calendarEvent));
  }, [copy, calendarEvent]);

  /*
   * Render.
   */

  return (
    <SimpleDropdown {...props}>
      {maybeRenderDownloadAction(props)}
      <SimpleDropdownItem
        onSelect={onCopyEventIdSelect}
        isDisabled={calendarEvent.visualStatus === CalendarEventVisualStatusesEnum.DRAFT}
      >
        <Trans id="calendarinvite-copyid">Copy event ID</Trans>
      </SimpleDropdownItem>
    </SimpleDropdown>
  );
};

function maybeRenderDownloadAction(props: CalendarInviteViewerMoreProps) {
  const {downloadICSAttachments, hasICSAttachments} = props;
  if (!hasICSAttachments) {
    return null;
  }

  return (
    <SimpleDropdownItem onSelect={() => downloadICSAttachments()}>
      <Trans id="calendarinvite-downloadics">Download .ics file</Trans>
    </SimpleDropdownItem>
  );
}
